import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  GetAllCustomWords,
  CreateCustomWord,
  EditCustomWord,
  DeleteCustomWord,
  GetFullCustomWordById,
  GetGroupedCustomWords,
  SaveCustomWordsBulk,
} from "../../services/customWords";

// Initial State
const initialState = {
  customWords: [],
  groupedWords: {}, // ✅ Added to support GlossaryTab
  loading: false,
  error: null,
};

// Slice
const customWordSlice = createSlice({
  name: "customWords",
  initialState,
  reducers: {
    setcustomWord: (state, { payload }) => {
      state.customWords = payload;
    },
    removesinglecustomWord: (state, { payload }) => {
      state.customWords = state.customWords.filter((i) => i.id !== payload); // ✅ Fixed key
    },
  },
  extraReducers: (builder) => {
    builder

      // ───── GET ALL ─────
      .addCase(getAllCustomWordsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllCustomWordsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.customWords = action.payload;
      })
      .addCase(getAllCustomWordsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // ───── CREATE ─────
      .addCase(createCustomWordAsync.fulfilled, (state, action) => {
        state.customWords.push(action.payload);
      })

      // ───── EDIT ─────
      .addCase(editCustomWordAsync.fulfilled, (state, action) => {
        const index = state.customWords.findIndex(
          (t) => t.id === action.payload.id
        );
        if (index !== -1) {
          state.customWords[index] = {
            ...state.customWords[index],
            ...action.payload.updated,
          };
        }
      })

      // ───── DELETE ─────
      .addCase(deleteCustomWordAsync.fulfilled, (state, action) => {
        state.customWords = state.customWords.filter(
          (t) => t.id !== action.payload
        );
      })

      // ───── GET FULL BY ID ─────
      .addCase(getFullCustomWordByIdAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFullCustomWordByIdAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.fullCustomWord = action.payload;
      })
      .addCase(getFullCustomWordByIdAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // ───── GET GROUPED ─────
      .addCase(getGroupedCustomWordsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getGroupedCustomWordsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.groupedWords = action.payload;
      })
      .addCase(getGroupedCustomWordsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // ───── SAVE BULK ─────
      .addCase(saveCustomWordsBulkAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveCustomWordsBulkAsync.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        // Optionally re-fetch or update groupedWords
      })
      .addCase(saveCustomWordsBulkAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

// ──────────────────────────────
// Async Thunks
// ──────────────────────────────

export const getAllCustomWordsAsync = createAsyncThunk(
  "customWords/getAllCustomWordsAsync",
  async () => {
    const response = await GetAllCustomWords();
    return response;
  }
);

export const createCustomWordAsync = createAsyncThunk(
  "customWords/createCustomWordAsync",
  async (payload) => {
    const response = await CreateCustomWord(payload);
    return response;
  }
);

export const editCustomWordAsync = createAsyncThunk(
  "customWords/editCustomWordAsync",
  async ({ id, payload }) => {
    const response = await EditCustomWord(payload, id);
    return { id, updated: response };
  }
);

export const deleteCustomWordAsync = createAsyncThunk(
  "customWords/deleteCustomWordAsync",
  async (id) => {
    await DeleteCustomWord(id);
    return id;
  }
);

export const getFullCustomWordByIdAsync = createAsyncThunk(
  "customWords/getFullCustomWordByIdAsync",
  async (id) => {
    const response = await GetFullCustomWordById(id);
    return response;
  }
);

export const getGroupedCustomWordsAsync = createAsyncThunk(
  "customWords/getGroupedCustomWordsAsync",
  async () => {
    const response = await GetGroupedCustomWords();
    return response;
  }
);

export const saveCustomWordsBulkAsync = createAsyncThunk(
  "customWords/saveCustomWordsBulkAsync",
  async (payload) => {
    const response = await SaveCustomWordsBulk(payload);
    return response;
  }
);

// ──────────────────────────────
// Exports
// ──────────────────────────────

export const { setcustomWord, removesinglecustomWord } =
  customWordSlice.actions;

export default customWordSlice.reducer;
